// TransactionDetail.jsx - Dengan fitur magnifier zoom dan button close yang berfungsi

import React, { useState, useEffect, useRef } from "react";
import "../../style/admin/TransactionDetail.css";

export default function TransactionDetail({ deposit, onClose, onProcess }) {
  const [showProofModal, setShowProofModal] = useState(false);
  const [userUSDTInfo, setUserUSDTInfo] = useState(null);
  const [showFullRecipient, setShowFullRecipient] = useState(false);
  const [showFullUser, setShowFullUser] = useState(false);
  const [proofError, setProofError] = useState(false);
  
  // State untuk magnifier
  const [showMagnifier, setShowMagnifier] = useState(false);
  const [magnifierPosition, setMagnifierPosition] = useState({ x: 0, y: 0 });
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  
  const modalRef = useRef(null);
  const proofModalRef = useRef(null);
  const imgRef = useRef(null);
  const magnifierSize = 150; // Ukuran magnifier dalam pixel
  const zoomLevel = 2.5; // Level zoom
  
  const recipientTimerRef = useRef(null);
  const userTimerRef = useRef(null);

  // Handler untuk klik di luar modal utama
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  // Handler untuk klik di luar proof modal - TIDAK MENUTUP MODAL UTAMA
  useEffect(() => {
    const handleClickOutsideProof = (event) => {
      // Hanya tutup proof modal, bukan modal utama
      if (showProofModal && proofModalRef.current && !proofModalRef.current.contains(event.target)) {
        setShowProofModal(false);
        // Reset magnifier state
        setShowMagnifier(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutsideProof);
    return () => {
      document.removeEventListener("mousedown", handleClickOutsideProof);
    };
  }, [showProofModal]);

  // Effect untuk mengambil data dari localStorage
  useEffect(() => {
    const storedDeposit = JSON.parse(localStorage.getItem("depositData"));
    if (storedDeposit?.userUSDTInfo) {
      setUserUSDTInfo(storedDeposit.userUSDTInfo);
    }
  }, []);

  // Cleanup timer saat component unmount
  useEffect(() => {
    return () => {
      if (recipientTimerRef.current) {
        clearTimeout(recipientTimerRef.current);
      }
      if (userTimerRef.current) {
        clearTimeout(userTimerRef.current);
      }
    };
  }, []);

  // Handler untuk mouse move pada gambar (magnifier)
  const handleMouseMove = (e) => {
    if (!imgRef.current) return;
    
    const { left, top, width, height } = imgRef.current.getBoundingClientRect();
    
    // Hitung posisi cursor relatif terhadap gambar
    const x = ((e.clientX - left) / width) * 100;
    const y = ((e.clientY - top) / height) * 100;
    
    // Batasi posisi cursor agar tidak keluar dari gambar
    const boundedX = Math.min(100, Math.max(0, x));
    const boundedY = Math.min(100, Math.max(0, y));
    
    setCursorPosition({ x: boundedX, y: boundedY });
    
    // Hitung posisi magnifier
    const magnifierX = e.clientX - left - magnifierSize / 2;
    const magnifierY = e.clientY - top - magnifierSize / 2;
    
    // Batasi posisi magnifier agar tidak keluar dari gambar
    const boundedMagnifierX = Math.min(width - magnifierSize, Math.max(0, magnifierX));
    const boundedMagnifierY = Math.min(height - magnifierSize, Math.max(0, magnifierY));
    
    setMagnifierPosition({ x: boundedMagnifierX, y: boundedMagnifierY });
  };

  // Handler untuk menutup proof modal
  const handleCloseProofModal = (e) => {
    e.stopPropagation();
    setShowProofModal(false);
    setShowMagnifier(false);
  };

  // Early return setelah semua hooks dipanggil
  if (!deposit) return null;

  // Helper: generate URL bukti transfer yang valid
  const getProofUrl = (file) => {
    if (!file) return null;
    
    try {
      const normalized = file.replace(/\\/g, "/");
      
      if (normalized.startsWith("http://") || normalized.startsWith("https://")) {
        return normalized;
      }
      
      if (normalized.startsWith("uploads/")) {
        return `https://blasterprime.com/${normalized}`;
      }
      
      return `https://blasterprime.com/uploads/${normalized}`;
    } catch (err) {
      console.error("Error generating proof URL:", err);
      return null;
    }
  };

  // Helper: format tanggal
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (isNaN(date)) return dateString;
    return date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Fungsi untuk mendapatkan usdt_address
  const getUserUSDTAddress = () => {
    const addr =
      (userUSDTInfo?.usdt_address && userUSDTInfo.usdt_address !== "0" && userUSDTInfo.usdt_address) ||
      (deposit.user?.usdt_address && deposit.user.usdt_address !== "0" && deposit.user.usdt_address) ||
      (deposit.deposit?.user?.usdt_address && deposit.deposit.user.usdt_address !== "0" && deposit.deposit.user.usdt_address) ||
      (deposit.usdt_address && deposit.usdt_address !== "0" && deposit.usdt_address) ||
      null;

    return addr ? addr : "Not Available";
  };

  // Fungsi untuk mendapatkan recipient wallet address
  const getRecipientWalletAddress = () => {
    return deposit.recipient_wallet || deposit.destination_address || "N/A";
  };

  // Fungsi untuk memformat alamat wallet
  const formatWalletAddress = (address) => {
    if (!address || address === "N/A" || address === "Not Available") return address;
    if (address.length <= 12) return address;
    return `${address.substring(0, 6)}...${address.substring(address.length - 6)}`;
  };

  // Handler untuk mouse down pada eye button recipient
  const handleRecipientMouseDown = () => {
    if (recipientTimerRef.current) {
      clearTimeout(recipientTimerRef.current);
    }
    recipientTimerRef.current = setTimeout(() => {
      setShowFullRecipient(true);
    }, 300);
  };

  const handleRecipientMouseUp = () => {
    if (recipientTimerRef.current) {
      clearTimeout(recipientTimerRef.current);
      recipientTimerRef.current = null;
    }
    setShowFullRecipient(false);
  };

  const handleUserMouseDown = () => {
    if (userTimerRef.current) {
      clearTimeout(userTimerRef.current);
    }
    userTimerRef.current = setTimeout(() => {
      setShowFullUser(true);
    }, 300);
  };

  const handleUserMouseUp = () => {
    if (userTimerRef.current) {
      clearTimeout(userTimerRef.current);
      userTimerRef.current = null;
    }
    setShowFullUser(false);
  };

  // Dapatkan URL proof
  const proofFile = deposit.evidence || deposit.proof_file;
  const proofUrl = getProofUrl(proofFile);

  return (
    <>
      <div className="transaction-details-container" onClick={onClose}>
        <div className="transaction-card" ref={modalRef} onClick={(e) => e.stopPropagation()}>
          {/* Header dengan Title dan Close Button */}
          <div className="transaction-header">
            <h3 className="transaction-title">Transaction Detail</h3>
            <button className="close-button" onClick={onClose}>
              ×
            </button>
          </div>

          {/* Status Badge */}
          <div className={`status-badge ${(deposit.status || "").toLowerCase().replace(" ", "-").replace("cancelled", "cancelled")}`}>
            {deposit.status || "Unknown Status"}
          </div>

          {/* Payment Info */}
          <div className="section">
            <h4>Payment</h4>
            <div className="info-row">
              <span>ID Deposit</span>
              <strong>{deposit.id || "N/A"}</strong>
            </div>
            <div className="info-row">
              <span>Payment Date</span>
              <strong>{formatDate(deposit.top_up_date || deposit.created_at)}</strong>
            </div>
            <div className="info-row">
              <span>Recipient Wallet Address</span>
              <strong className="wallet-address-container">
                <span className="wallet-address-text">
                  {showFullRecipient ? getRecipientWalletAddress() : formatWalletAddress(getRecipientWalletAddress())}
                </span>
                <button 
                  className="icon-btn eye-btn" 
                  onMouseDown={handleRecipientMouseDown}
                  onMouseUp={handleRecipientMouseUp}
                  onMouseLeave={handleRecipientMouseUp}
                  onTouchStart={handleRecipientMouseDown}
                  onTouchEnd={handleRecipientMouseUp}
                  onTouchCancel={handleRecipientMouseUp}
                  title="Press and hold to see full address"
                >
                  👁
                </button>
              </strong>
            </div>
            <div className="info-row">
              <span>User's Wallet Address</span>
              <strong className="wallet-address-container">
                <span className="wallet-address-text">
                  {showFullUser ? getUserUSDTAddress() : formatWalletAddress(getUserUSDTAddress())}
                </span>
                <button 
                  className="icon-btn eye-btn" 
                  onMouseDown={handleUserMouseDown}
                  onMouseUp={handleUserMouseUp}
                  onMouseLeave={handleUserMouseUp}
                  onTouchStart={handleUserMouseDown}
                  onTouchEnd={handleUserMouseUp}
                  onTouchCancel={handleUserMouseUp}
                  title="Press and hold to see full address"
                >
                  👁
                </button>
              </strong>
            </div>

            {/* TRANSFER EVIDENCE */}
            <div className="info-row">
              <span>Transfer Evidence</span>
              <strong>
                {proofFile ? (
                  <button className="proof-link-btn" onClick={() => setShowProofModal(true)}>
                    Click to see the proof
                  </button>
                ) : deposit.tx_hash ? (
                  <span>No proof uploaded (TX Hash: {deposit.tx_hash})</span>
                ) : (
                  <span>No proof uploaded</span>
                )}
              </strong>
            </div>

            <div className="info-row">
              <span>Network</span>
              <strong>{deposit.network || "N/A"}</strong>
            </div>
            <div className="info-row">
              <span>Amount</span>
              <strong>{deposit.amount ? `$${deposit.amount}` : "N/A"}</strong>
            </div>

            {/* Transaction Hash jika ada */}
            {deposit.tx_hash && (
              <div className="info-row">
                <span>Transaction Hash</span>
                <strong>{deposit.tx_hash}</strong>
              </div>
            )}

            {/* Transaction Link jika ada */}
            {deposit.tx_link && (
              <div className="info-row">
                <span>Transaction Link</span>
                <a href={deposit.tx_link} target="_blank" rel="noreferrer" className="tx-link">
                  View on Block Explorer ↗
                </a>
              </div>
            )}
          </div>

          {/* User Request */}
          <div className="section">
            <h4>User's Request</h4>
            <div className="info-row">
              <span>Full Name</span>
              <strong>{deposit.user_request || deposit.username || "N/A"}</strong>
            </div>
            <div className="info-row">
              <span>Email</span>
              <strong>{deposit.email || "N/A"}</strong>
            </div>
          </div>

          {/* Admin Notes jika ada */}
          {deposit.admin_notes && (
            <div className="section">
              <h4>Admin Notes</h4>
              <div className="info-row">
                <span>Notes</span>
                <strong>{deposit.admin_notes}</strong>
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="action-buttons-tr-detail">
            <button className="btn secondary" onClick={onClose}>
              Back
            </button>
            {(deposit.status === "Checking Deposit" || deposit.status === "checking") && (
              <>
                <button
                  className="btn danger"
                  onClick={() => {
                    onProcess(deposit.id, "reject");
                    onClose();
                  }}
                >
                  Reject
                </button>
                <button
                  className="btn primary"
                  onClick={() => {
                    onProcess(deposit.id, "approve");
                    onClose();
                  }}
                >
                  Approve
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Modal Popup untuk Proof Evidence dengan Magnifier */}
      {showProofModal && proofFile && (
        <div className="modal-overlay-proof" onClick={handleCloseProofModal}>
          <div 
            className="modal-content-proof proof-modal" 
            ref={proofModalRef}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header Proof Modal dengan Title dan Close Button */}
            <div className="proof-modal-header">
              <h3>Transfer Evidence</h3>
              <button 
                className="proof-close-btn" 
                onClick={handleCloseProofModal}
                title="Close"
              >
                ×
              </button>
            </div>
            
            <div 
              className="image-container"
              style={{ position: 'relative', display: 'inline-block' }}
            >
              <img
                ref={imgRef}
                src={proofUrl}
                alt="Proof of Transfer"
                className="proof-image"
                onMouseEnter={() => setShowMagnifier(true)}
                onMouseLeave={() => setShowMagnifier(false)}
                onMouseMove={handleMouseMove}
                onError={(e) => {
                  console.error("Error loading proof image:", e);
                  setProofError(true);
                  e.target.onerror = null;
                  e.target.src = "https://via.placeholder.com/400x300?text=Proof+Not+Found";
                  e.target.alt = "Proof image not available";
                }}
                onLoad={() => {
                  console.log("Proof image loaded successfully");
                  setProofError(false);
                }}
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '400px',
                  cursor: 'crosshair'
                }}
              />
              
              {/* Magnifier */}
              {showMagnifier && !proofError && (
                <div
                  className="magnifier"
                  style={{
                    position: 'absolute',
                    left: `${magnifierPosition.x}px`,
                    top: `${magnifierPosition.y}px`,
                    width: `${magnifierSize}px`,
                    height: `${magnifierSize}px`,
                    border: '2px solid #0044cc',
                    borderRadius: '50%',
                    backgroundColor: 'white',
                    backgroundImage: `url('${proofUrl}')`,
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: `${imgRef.current ? imgRef.current.width * zoomLevel : 400 * zoomLevel}px ${imgRef.current ? imgRef.current.height * zoomLevel : 300 * zoomLevel}px`,
                    backgroundPosition: `-${cursorPosition.x * (zoomLevel - 1) * (imgRef.current ? imgRef.current.width / 100 : 4)}px -${cursorPosition.y * (zoomLevel - 1) * (imgRef.current ? imgRef.current.height / 100 : 3)}px`,
                    pointerEvents: 'none',
                    boxShadow: '0 0 10px rgba(0,0,0,0.3)',
                    zIndex: 1000
                  }}
                />
              )}
            </div>
            
            {/* Kontrol Zoom */}
            <div style={{ 
              marginTop: '15px', 
              textAlign: 'center',
              color: '#666',
              fontSize: '14px'
            }}>
              <p>👆 Hover over the image to zoom (2.5x)</p>
            </div>
            
            {/* Tampilkan URL sebagai teks */}
            {/* <div style={{ 
              marginTop: '15px', 
              padding: '10px', 
              backgroundColor: '#f5f5f5', 
              borderRadius: '4px',
              wordBreak: 'break-all',
              fontSize: '12px'
            }}>
              <strong>URL:</strong> {proofUrl}
            </div> */}
            
            {proofError && (
              <p className="proof-error-message" style={{ color: 'red', marginTop: '10px' }}>
                Failed to load proof image. The file may be corrupted or missing.
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );
}